import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Play, Pause, SkipForward, SkipBack, Plus, Trash2, Edit2, RefreshCw,
  GripVertical, Tv, Music, BookOpen, MessageSquare, Gift, FileText, List,
  Timer, TimerOff, Clock, Save, Check, AlertCircle
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const fmt = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

const getTypeIcon = (type) => {
  const icons = { welcome: Tv, hymn: Music, bible: BookOpen, announcement: MessageSquare, offering: Gift };
  const Icon = icons[type] || FileText;
  return <Icon className="w-4 h-4" />;
};

const getTypeColor = (type) => {
  const colors = {
    welcome: 'bg-blue-100 text-blue-700', hymn: 'bg-purple-100 text-purple-700',
    bible: 'bg-amber-100 text-amber-700', announcement: 'bg-green-100 text-green-700',
    offering: 'bg-rose-100 text-rose-700'
  };
  return colors[type] || 'bg-slate-100 text-slate-700';
};

const itemTypes = [
  { value: 'welcome', label: 'Welcome Display' },
  { value: 'hymn', label: 'Hymn' },
  { value: 'bible', label: 'Bible Reading' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'offering', label: 'Offering' },
];

const defaultFlow = [
  { id: 1, type: 'welcome', title: 'Welcome Display', data: {} },
  { id: 2, type: 'hymn', title: 'Opening Hymn', data: { hymn_number: 1 } },
  { id: 3, type: 'bible', title: 'Bible Reading', data: { reference: 'John 3:16' } },
  { id: 4, type: 'announcement', title: 'Announcements', data: {} },
  { id: 5, type: 'offering', title: 'Offering', data: {} },
  { id: 6, type: 'hymn', title: 'Closing Hymn', data: { hymn_number: 2 } },
];

/* ── Sortable Row ───────────────────────────────────────── */
const SortableItem = ({ item, index, currentIndex, itemElapsed, itemDuration, onShow, onEdit, onDelete }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.85 : 1,
  };

  const isLive = currentIndex === index;
  const isDone = itemDuration > 0 && !isLive;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`flow-item-${index}`}
      className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
        isDragging ? 'bg-blue-50 shadow-xl ring-2 ring-blue-400'
        : isLive   ? 'bg-blue-600 text-white shadow-lg'
        : isDone   ? 'bg-slate-50/80 hover:bg-slate-100'
        :            'bg-slate-50 hover:bg-slate-100'
      }`}
    >
      {/* Drag handle */}
      <button {...attributes} {...listeners} data-testid={`drag-handle-${index}`}
        className={`p-1.5 rounded-lg cursor-grab active:cursor-grabbing touch-none ${
          isLive ? 'hover:bg-white/20 text-white/70' : 'hover:bg-slate-200 text-slate-400'
        }`} title="Drag to reorder">
        <GripVertical className="w-5 h-5" />
      </button>

      {/* Number badge */}
      <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold shrink-0 ${
        isLive ? 'bg-white/20' : isDone ? 'bg-green-100 text-green-700' : 'bg-slate-200'
      }`}>
        {isDone ? '\u2713' : index + 1}
      </div>

      {/* Type icon */}
      <div className={`p-2 rounded-lg shrink-0 ${isLive ? 'bg-white/20' : getTypeColor(item.type)}`}>
        {getTypeIcon(item.type)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onShow(item, index)}>
        <p className={`font-medium truncate ${isLive ? 'text-white' : isDone ? 'text-slate-500' : 'text-slate-800'}`}>{item.title}</p>
        {item.data?.reference && <p className={`text-sm truncate ${isLive ? 'text-white/70' : 'text-slate-500'}`}>{item.data.reference}</p>}
        {item.data?.hymn_number && <p className={`text-sm truncate ${isLive ? 'text-white/70' : 'text-slate-500'}`}>Hymn #{item.data.hymn_number}</p>}
      </div>

      {/* Timer / Duration */}
      {isLive && itemElapsed > 0 && (
        <div data-testid={`item-timer-${index}`}
          className="flex items-center gap-1.5 px-3 py-1 bg-white/15 rounded-full shrink-0 font-mono text-sm text-white">
          <Clock className="w-3.5 h-3.5 text-green-300" />
          {fmt(itemElapsed)}
        </div>
      )}
      {isDone && itemDuration > 0 && (
        <span data-testid={`item-duration-${index}`}
          className="text-xs text-slate-400 font-mono shrink-0">{fmt(itemDuration)}</span>
      )}

      {/* Live badge */}
      {isLive && (
        <div className="flex items-center gap-1 text-white/80 text-sm shrink-0">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          Live
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-1 shrink-0">
        <button data-testid={`edit-flow-item-${index}`} onClick={() => onEdit(item)}
          className={`p-2 rounded-lg ${isLive ? 'hover:bg-white/20' : 'hover:bg-slate-200'}`}>
          <Edit2 className="w-4 h-4" />
        </button>
        <button data-testid={`delete-flow-item-${index}`} onClick={() => onDelete(item.id)}
          className={`p-2 rounded-lg ${isLive ? 'hover:bg-white/20 text-white' : 'hover:bg-red-100 text-red-600'}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ── Main Panel ─────────────────────────────────────────── */
const ServiceFlowPanel = ({ onRefresh }) => {
  const [flowItems, setFlowItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({ type: 'welcome', title: '', data: {} });

  // Hymn verse navigation state
  const [currentVerse, setCurrentVerse] = useState(0);
  const [totalVerses, setTotalVerses] = useState(0);
  const [hymnLanguage, setHymnLanguage] = useState('english');

  // Timer state
  const [serviceRunning, setServiceRunning] = useState(false);
  const [serviceStart, setServiceStart] = useState(null);       // total service start
  const [serviceElapsed, setServiceElapsed] = useState(0);
  const [itemStart, setItemStart] = useState(null);             // current item start
  const [itemElapsed, setItemElapsed] = useState(0);
  const [itemDurations, setItemDurations] = useState({});       // id -> seconds spent
  const tickRef = useRef(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const savedSnapshotRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Tick every second
  useEffect(() => {
    if (serviceRunning) {
      tickRef.current = setInterval(() => {
        const now = Date.now();
        if (serviceStart) setServiceElapsed(Math.floor((now - serviceStart) / 1000));
        if (itemStart) setItemElapsed(Math.floor((now - itemStart) / 1000));
      }, 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [serviceRunning, serviceStart, itemStart]);

  useEffect(() => {
    // Load from database first, fallback to localStorage, then default
    const loadFlow = async () => {
      try {
        const res = await axios.get(`${API}/service-flow/load`);
        if (res.data?.found && res.data.items?.length > 0) {
          setFlowItems(res.data.items);
          savedSnapshotRef.current = JSON.stringify(res.data.items);
          if (res.data.updated_at) setLastSavedAt(res.data.updated_at);
          return;
        }
      } catch (e) { /* fallback to localStorage */ }
      // Fallback to localStorage
      const saved = localStorage.getItem('serviceFlow');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setFlowItems(parsed);
          setHasUnsavedChanges(true); // localStorage data isn't in DB yet
          return;
        } catch {}
      }
      setFlowItems(defaultFlow);
      setHasUnsavedChanges(true);
    };
    loadFlow();
  }, []);

  useEffect(() => {
    if (flowItems.length > 0) {
      localStorage.setItem('serviceFlow', JSON.stringify(flowItems));
      // Track if current state differs from last saved
      if (savedSnapshotRef.current && JSON.stringify(flowItems) !== savedSnapshotRef.current) {
        setHasUnsavedChanges(true);
      }
    }
  }, [flowItems]);

  // Auto-sync flow items to backend whenever they change (for remote control)
  useEffect(() => {
    if (flowItems.length === 0) return;
    const syncToBackend = async () => {
      try {
        await axios.post(`${API}/display/remote-flow/sync`, {
          items: flowItems,
          current_index: currentIndex,
          language: hymnLanguage
        });
      } catch (e) { /* silent */ }
    };
    syncToBackend();
  }, [flowItems, currentIndex, hymnLanguage]);

  const recordItemDuration = useCallback((idx) => {
    if (idx < 0 || !itemStart) return;
    const id = flowItems[idx]?.id;
    if (!id) return;
    const dur = Math.floor((Date.now() - itemStart) / 1000);
    setItemDurations(prev => ({ ...prev, [id]: (prev[id] || 0) + dur }));
  }, [flowItems, itemStart]);

  const handleShowItem = async (item, index) => {
    // Record duration of previous item
    if (currentIndex >= 0) recordItemDuration(currentIndex);

    // Start service timer on first activation
    if (!serviceRunning) {
      setServiceRunning(true);
      setServiceStart(Date.now());
    }

    setCurrentIndex(index);
    setItemStart(Date.now());
    setItemElapsed(0);

    // Reset verse state
    setCurrentVerse(0);
    setTotalVerses(0);

    try {
      const payload = { ...(item.data || {}) };
      if (item.type === 'hymn') payload.language = hymnLanguage;
      if (item.type === 'bible') payload.language = hymnLanguage;
      const res = await axios.post(`${API}/display/show/${item.type}`, payload);
      if ((item.type === 'hymn' || item.type === 'bible') && res.data?.total_verses) {
        setTotalVerses(res.data.total_verses);
        setCurrentVerse(0);
      }
    } catch (e) { console.error(e); }
  };

  const handleVerseChange = async (verseIndex) => {
    if (verseIndex < 0 || verseIndex >= totalVerses) return;
    setCurrentVerse(verseIndex);
    const activeItem = flowItems[currentIndex];
    try {
      if (activeItem?.type === 'bible') {
        await axios.post(`${API}/display/bible/verse/${verseIndex}`);
      } else {
        await axios.post(`${API}/display/hymn/verse/${verseIndex}`);
      }
    } catch (e) { console.error(e); }
  };

  const handleLanguageSwitch = async (lang) => {
    setHymnLanguage(lang);
    if (currentIndex < 0) return;
    const item = flowItems[currentIndex];
    
    if (item?.type === 'hymn') {
      setCurrentVerse(0);
      try {
        const res = await axios.post(`${API}/display/show/hymn`, {
          ...(item.data || {}), language: lang, current_verse: 0
        });
        if (res.data?.total_verses) setTotalVerses(res.data.total_verses);
      } catch (e) { console.error(e); }
    } else if (item?.type === 'bible') {
      setCurrentVerse(0);
      try {
        const res = await axios.post(`${API}/display/bible/language/${lang}`);
        if (res.data?.total_verses) setTotalVerses(res.data.total_verses);
      } catch (e) { console.error(e); }
    }
  };

  const handleNext = () => {
    if (currentIndex < flowItems.length - 1) handleShowItem(flowItems[currentIndex + 1], currentIndex + 1);
  };
  const handlePrevious = () => {
    if (currentIndex > 0) handleShowItem(flowItems[currentIndex - 1], currentIndex - 1);
  };

  const handleResetTimers = () => {
    if (!window.confirm('Reset all timers?')) return;
    setServiceRunning(false);
    setServiceStart(null);
    setServiceElapsed(0);
    setItemStart(null);
    setItemElapsed(0);
    setItemDurations({});
    setCurrentIndex(-1);
  };

  const handleAddItem = () => {
    if (!newItem.title) { alert('Please enter a title'); return; }
    setFlowItems([...flowItems, { ...newItem, id: Date.now() }]);
    setNewItem({ type: 'welcome', title: '', data: {} });
    setShowAddModal(false);
  };

  const handleSaveFlow = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/service-flow/save`, { name: 'default', items: flowItems });
      savedSnapshotRef.current = JSON.stringify(flowItems);
      setHasUnsavedChanges(false);
      setLastSavedAt(new Date().toISOString());
    } catch (e) {
      console.error('Save error:', e);
      alert('Failed to save service flow. Please try again.');
    }
    setIsSaving(false);
  };

  const handleDeleteItem = (id) => {
    if (!window.confirm('Delete this item?')) return;
    setFlowItems(flowItems.filter(f => f.id !== id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFlowItems((items) => {
      const oldIndex = items.findIndex(i => i.id === active.id);
      const newIndex = items.findIndex(i => i.id === over.id);
      const reordered = arrayMove(items, oldIndex, newIndex);
      if (currentIndex === oldIndex) setCurrentIndex(newIndex);
      else if (currentIndex >= Math.min(oldIndex, newIndex) && currentIndex <= Math.max(oldIndex, newIndex))
        setCurrentIndex(prev => oldIndex < newIndex ? prev - 1 : prev + 1);
      return reordered;
    });
  };

  return (
    <div className="space-y-5">
      {/* Header + Timer Bar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Service Flow Control</h3>
          <p className="text-sm text-slate-500">Drag to reorder &middot; Click to display &middot; Timers track duration</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="flow-prev-btn" onClick={handlePrevious} disabled={currentIndex <= 0}
            className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50" title="Previous">
            <SkipBack className="w-5 h-5" />
          </button>
          <button data-testid="flow-next-btn" onClick={handleNext} disabled={currentIndex >= flowItems.length - 1}
            className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50" title="Next">
            <SkipForward className="w-5 h-5" />
          </button>
          <button data-testid="flow-add-btn" onClick={() => setShowAddModal(true)}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Item
          </button>
          <button data-testid="flow-save-btn" onClick={handleSaveFlow} disabled={isSaving}
            className={`px-3 py-2 rounded-lg flex items-center gap-2 font-medium transition-all ${
              hasUnsavedChanges
                ? 'bg-amber-500 text-white hover:bg-amber-600 animate-pulse'
                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            } disabled:opacity-50`}
            title={lastSavedAt ? `Last saved: ${new Date(lastSavedAt).toLocaleTimeString()}` : 'Save flow to database'}>
            {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : hasUnsavedChanges ? <Save className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            {isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save' : 'Saved'}
          </button>
          <button onClick={() => { if (window.confirm('Reset to default service flow?')) { setFlowItems(defaultFlow); localStorage.removeItem('serviceFlow'); handleResetTimers(); setHasUnsavedChanges(true); } }}
            className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 text-slate-600" title="Reset">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Service Timer Bar */}
      <div className={`flex items-center justify-between px-5 py-3 rounded-xl border transition-colors ${
        serviceRunning
          ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'
          : 'bg-slate-50 border-slate-200'
      }`}>
        <div className="flex items-center gap-3">
          <Timer className={`w-5 h-5 ${serviceRunning ? 'text-emerald-600' : 'text-slate-400'}`} />
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Service Timer</p>
            <p data-testid="service-timer" className={`text-2xl font-mono font-bold ${serviceRunning ? 'text-emerald-700' : 'text-slate-400'}`}>
              {fmt(serviceElapsed)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {serviceRunning && currentIndex >= 0 && (
            <div className="text-right">
              <p className="text-xs text-slate-500">Current: <span className="font-medium text-slate-700">{flowItems[currentIndex]?.title}</span></p>
              <p data-testid="current-item-timer" className="text-lg font-mono font-semibold text-blue-600">{fmt(itemElapsed)}</p>
            </div>
          )}
          {serviceRunning && (
            <button data-testid="reset-timers-btn" onClick={handleResetTimers}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50">
              <TimerOff className="w-4 h-4" /> Reset
            </button>
          )}
          {!serviceRunning && (
            <p className="text-sm text-slate-400 italic">Click any item to start the service</p>
          )}
        </div>
      </div>

      {/* Sortable list */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={flowItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2" data-testid="service-flow-list">
            {flowItems.map((item, index) => (
              <SortableItem
                key={item.id}
                item={item}
                index={index}
                currentIndex={currentIndex}
                itemElapsed={currentIndex === index ? itemElapsed : 0}
                itemDuration={itemDurations[item.id] || 0}
                onShow={handleShowItem}
                onEdit={(itm) => setEditingItem({ ...itm })}
                onDelete={handleDeleteItem}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Hymn/Bible Verse Navigation - shows when a hymn or bible item is active */}
      {currentIndex >= 0 && (flowItems[currentIndex]?.type === 'hymn' || flowItems[currentIndex]?.type === 'bible') && totalVerses > 0 && (
        <div data-testid="verse-navigation" className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-purple-800">
              Verse {currentVerse + 1} of {totalVerses}
            </p>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg overflow-hidden border border-purple-200" data-testid="language-toggle">
                <button
                  data-testid="lang-english-btn"
                  onClick={() => handleLanguageSwitch('english')}
                  className={`px-3 py-1.5 text-xs font-bold transition-all ${
                    hymnLanguage === 'english'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  English
                </button>
                <button
                  data-testid="lang-both-btn"
                  onClick={() => handleLanguageSwitch('both')}
                  className={`px-3 py-1.5 text-xs font-bold transition-all border-l border-r border-purple-200 ${
                    hymnLanguage === 'both'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  Both
                </button>
                <button
                  data-testid="lang-yoruba-btn"
                  onClick={() => handleLanguageSwitch('yoruba')}
                  className={`px-3 py-1.5 text-xs font-bold transition-all ${
                    hymnLanguage === 'yoruba'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  Yoruba
                </button>
              </div>
              <p className="text-xs text-purple-600">{flowItems[currentIndex]?.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="verse-prev-btn"
              onClick={() => handleVerseChange(currentVerse - 1)}
              disabled={currentVerse <= 0}
              className="px-3 py-2 bg-white border border-purple-200 rounded-lg text-purple-700 hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              <SkipBack className="w-4 h-4" />
            </button>
            <div className="flex-1 flex gap-1.5 justify-center flex-wrap">
              {Array.from({ length: totalVerses }, (_, i) => (
                <button
                  key={i}
                  data-testid={`verse-btn-${i}`}
                  onClick={() => handleVerseChange(i)}
                  className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                    i === currentVerse
                      ? 'bg-purple-600 text-white shadow-md scale-110'
                      : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-100'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              data-testid="verse-next-btn"
              onClick={() => handleVerseChange(currentVerse + 1)}
              disabled={currentVerse >= totalVerses - 1}
              className="px-3 py-2 bg-white border border-purple-200 rounded-lg text-purple-700 hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {flowItems.length === 0 && (
        <div className="text-center py-8 bg-slate-50 rounded-xl">
          <List className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No items in service flow</p>
          <button onClick={() => setShowAddModal(true)}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add First Item</button>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md m-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Add Service Item</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select data-testid="add-item-type" value={newItem.type}
                  onChange={(e) => setNewItem({ ...newItem, type: e.target.value, title: itemTypes.find(t => t.value === e.target.value)?.label || '' })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl">
                  {itemTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input data-testid="add-item-title" type="text" value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  placeholder="e.g., Opening Hymn" className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
              </div>
              {newItem.type === 'hymn' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hymn Number</label>
                  <input type="number" value={newItem.data?.hymn_number || ''}
                    onChange={(e) => setNewItem({ ...newItem, data: { hymn_number: parseInt(e.target.value) || 1 } })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
                </div>
              )}
              {newItem.type === 'bible' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
                  <input type="text" value={newItem.data?.reference || ''}
                    onChange={(e) => setNewItem({ ...newItem, data: { reference: e.target.value } })}
                    placeholder="e.g., John 3:16" className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button data-testid="confirm-add-item" onClick={handleAddItem}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700">Add Item</button>
                <button onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingItem(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md m-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Edit Service Item</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select value={editingItem.type} onChange={(e) => setEditingItem({ ...editingItem, type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl">
                  {itemTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input type="text" value={editingItem.title} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
              </div>
              {editingItem.type === 'hymn' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hymn Number</label>
                  <input type="number" value={editingItem.data?.hymn_number || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, data: { ...editingItem.data, hymn_number: parseInt(e.target.value) || 1 } })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
                </div>
              )}
              {editingItem.type === 'bible' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
                  <input type="text" value={editingItem.data?.reference || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, data: { ...editingItem.data, reference: e.target.value } })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={() => { setFlowItems(flowItems.map(f => f.id === editingItem.id ? editingItem : f)); setEditingItem(null); }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700">Save Changes</button>
                <button onClick={() => setEditingItem(null)}
                  className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceFlowPanel;
